import type { StatusMessageKind } from '@konvoi/shared';

/**
 * Die Zeichen der Meldungen.
 *
 * ## Warum gezeichnet und nicht aus der Schrift
 *
 * Vorher standen hier Schriftzeichen: ✓ ❚❚ ⛽ ⚠ ? ▼ ■ ▲. Das hatte drei Fehler
 * auf einmal:
 *
 *   * **Farbe.** ⛽ ist auf iOS ein farbiges Emoji und keine Schrift. Zwischen
 *     lauter schwarzen Zeichen leuchtete eine rote Zapfsäule.
 *   * **Größe und Lage.** Jedes Zeichen bringt seine eigenen Maße mit. ❚❚ steht
 *     hoch und schmal, ▲ klein und tief, ⚠ breit — in einer Reihe untereinander
 *     sieht das aus wie zusammengesucht.
 *   * **Doppelte Bedeutung.** ❚❚, ⚠ und ? standen für je zwei verschiedene
 *     Meldungen. „Panne" und „Gefahrenstelle" trugen dasselbe Zeichen.
 *
 * Gezeichnet haben alle dieselbe Fläche (24 × 24), dieselbe Strichstärke und
 * dieselbe Farbe wie die Schrift daneben. Und jede Meldung ihr eigenes Bild.
 *
 * ## Was in der Fläche steht, zählt auch
 *
 * Derselbe Rahmen genügt nicht — die Zeichnung darin muss ihn ähnlich füllen
 * und auf der Mitte (y = 12) sitzen. „Bitte aufschließen" tat das nicht: Seine
 * beiden Winkel lagen zwischen y 4,5 und 16,5, also mit der Mitte bei 10,5 und
 * nur halb so hoch wie der Rahmen. Neben einer großen Meldung sah das aus wie
 * verrutscht, und genau so ist es am 14.08.2026 aufgefallen. „Bitte langsamer"
 * hatte denselben Fehler spiegelbildlich nach unten.
 *
 * Wer hier ein Zeichen ändert, prüft beides: Mitte auf 12, Höhe etwa 13 bis 17
 * von 24.
 *
 * ## Warum diese Bilder
 *
 * „Panne" trägt das **Warndreieck** — das Ding, das man in Deutschland bei
 * einer Panne hinter dem Wagen aufstellt. Näher kommt kein Zeichen an die Sache
 * heran. „Gefahrenstelle" bekommt deshalb das Ausrufezeichen im Kreis: zwei
 * Warnungen, zwei Silhouetten.
 */

const STRICH = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function Rahmen({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg
      className="meldezeichen"
      viewBox="0 0 24 24"
      width="26"
      height="26"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Das Fragezeichen — einmal für sich, einmal im Kreis. */
function Fragezeichen(): React.JSX.Element {
  return (
    <>
      <path d="M8.6 9a3.4 3.4 0 1 1 3.4 3.6v1.6" {...STRICH} />
      <circle cx="12" cy="18.4" r="1.25" fill="currentColor" />
    </>
  );
}

export function MeldungsZeichen({ kind }: { kind: StatusMessageKind }): React.JSX.Element {
  switch (kind) {
    // --- Aus der Kolonne ---------------------------------------------------
    case 'ok':
      return (
        <Rahmen>
          <path d="M4.5 12.8 9.4 17.7 19.5 6.8" {...STRICH} />
        </Rahmen>
      );

    case 'break':
      return (
        <Rahmen>
          {/* Zwei Balken — das Zeichen für Anhalten auf jedem Gerät. */}
          <rect x="7" y="4.5" width="3.6" height="15" rx="1.2" fill="currentColor" />
          <rect x="13.4" y="4.5" width="3.6" height="15" rx="1.2" fill="currentColor" />
        </Rahmen>
      );

    case 'fuel':
      return (
        <Rahmen>
          {/* Zapfsäule: Gehäuse mit Anzeige, rechts der Schlauchbogen. */}
          <path d="M4.5 20V6a1.5 1.5 0 0 1 1.5-1.5h5A1.5 1.5 0 0 1 12.5 6v14" {...STRICH} />
          <path d="M3 20h11" {...STRICH} />
          <path d="M6.8 8h3.4" {...STRICH} />
          <path d="M12.5 10.5h3.2v5.2a1.9 1.9 0 0 0 3.8 0V8.6l-2.2-2.2" {...STRICH} />
        </Rahmen>
      );

    case 'breakdown':
      return (
        <Rahmen>
          {/* Das Warndreieck, das bei einer Panne hinter dem Wagen steht. */}
          <path d="M12 4.2 21 19.5H3z" {...STRICH} />
        </Rahmen>
      );

    case 'lost':
      return (
        <Rahmen>
          <Fragezeichen />
        </Rahmen>
      );

    case 'slow_down':
      return (
        <Rahmen>
          {/* Zwei Winkel nach unten: langsamer werden. Mitte auf 12. */}
          <path d="M4.5 5.5 12 12.5 19.5 5.5" {...STRICH} />
          <path d="M4.5 11.5 12 18.5 19.5 11.5" {...STRICH} />
        </Rahmen>
      );

    // --- Von der Führung an die Kolonne ------------------------------------
    case 'stop':
      return (
        <Rahmen>
          <rect x="5.5" y="5.5" width="13" height="13" rx="2" fill="currentColor" />
        </Rahmen>
      );

    case 'break_soon':
      return (
        <Rahmen>
          {/* Die Uhr sagt „gleich" — die Balken sagten nur „Pause", und die
              hatte „Pause nötig" schon. */}
          <circle cx="12" cy="12" r="8.6" {...STRICH} />
          <path d="M12 6.8V12l3.6 2.2" {...STRICH} />
        </Rahmen>
      );

    case 'close_up':
      return (
        <Rahmen>
          {/* Zwei Winkel nach oben — das Gegenstück zu „Bitte langsamer". */}
          <path d="M4.5 18.5 12 11.5 19.5 18.5" {...STRICH} />
          <path d="M4.5 12.5 12 5.5 19.5 12.5" {...STRICH} />
        </Rahmen>
      );

    case 'hazard':
      return (
        <Rahmen>
          <circle cx="12" cy="12" r="8.6" {...STRICH} />
          <path d="M12 7.4v5.4" {...STRICH} />
          <circle cx="12" cy="16.4" r="1.25" fill="currentColor" />
        </Rahmen>
      );

    case 'check_in':
      return (
        <Rahmen>
          {/* Dieselbe Frage wie „Verfahren", aber an alle gerichtet: im Kreis. */}
          <circle cx="12" cy="12" r="9.4" {...STRICH} strokeWidth={1.8} />
          <path d="M9.6 10.2a2.5 2.5 0 1 1 2.5 2.6v1" {...STRICH} strokeWidth={1.8} />
          <circle cx="12.1" cy="16.4" r="1" fill="currentColor" />
        </Rahmen>
      );
  }
}
